import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { CharityBootstrapService } from '@app/core/charity-bootstrap/charity-bootstrap.service';
import {
  DONATE_FONT_CONFIG,
  donateFontMonoStack,
  donateFontSansStack,
  donateGoogleFontsHref,
} from './donate-font/donate-font.config';
import { filter, Subscription } from 'rxjs';
import { HeaderHostComponent } from '../slots/header/header-host.component';
import { ContentHostComponent } from '../slots/content/content-host.component';
import { FooterHostComponent } from '../slots/footer/footer-host.component';

@Component({
  selector: 'gb-layout-shell',
  standalone: true,
  imports: [HeaderHostComponent, ContentHostComponent, FooterHostComponent],
  providers: [CharityBootstrapService],
  template: `
    <div class="flex min-h-screen flex-col">
      <gb-header />
      <main class="flex-1">
        <gb-content />
      </main>
      <gb-footer />
    </div>
  `,
  styleUrls: ['./layout-shell.component.scss'],
  host: {
    class: 'block min-h-screen',
    '[class.donate-app]': 'isDonateRoute',
    '[class.donate-app--custom-fonts]': 'donateFontsActive',
    '[class.font-donate-sans]': 'donateFontsActive',
    '[style.--donate-font-sans]': 'donateFontSans',
    '[style.--donate-font-mono]': 'donateFontMono',
  },
})
export class LayoutShellComponent implements OnInit, OnDestroy {
  private readonly charityBootstrap = inject(CharityBootstrapService);
  private readonly router = inject(Router);
  private routeSub?: Subscription;

  isDonateRoute = false;
  donateFontsActive = false;
  donateFontSans: string | null = null;
  donateFontMono: string | null = null;

  constructor(@Inject(PLATFORM_ID) private platformId: object) {
    this.syncDonateFonts(this.router.url);
  }

  ngOnInit(): void {
    this.charityBootstrap.start();
    this.routeSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.syncDonateFonts(e.urlAfterRedirects));
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    this.clearDocumentDonateFonts();
  }

  private syncDonateFonts(url: string): void {
    this.isDonateRoute = this.isDonateUrl(url);
    this.donateFontsActive = this.isDonateRoute && DONATE_FONT_CONFIG.enabled;
    this.donateFontSans = this.donateFontsActive ? donateFontSansStack() : null;
    this.donateFontMono = this.donateFontsActive ? donateFontMonoStack() : null;

    if (this.donateFontsActive) {
      this.loadDonateFonts();
      this.applyDocumentDonateFonts();
    } else {
      this.clearDocumentDonateFonts();
    }
  }

  private isDonateUrl(url: string): boolean {
    const path = url.split('?')[0];
    return (
      path === '/donate' ||
      path.startsWith('/donate/') ||
      path === '/checkout' ||
      path.startsWith('/checkout/')
    );
  }

  private loadDonateFonts(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const href = donateGoogleFontsHref();
    if (!href || document.getElementById('donate-google-fonts')) {
      return;
    }

    const link = document.createElement('link');
    link.id = 'donate-google-fonts';
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  }

  /** Mirror scope onto <html> so body-portaled dialogs/sheets inherit Inter. */
  private applyDocumentDonateFonts(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const root = document.documentElement;
    root.classList.add('donate-app--custom-fonts');
    root.style.setProperty('--donate-font-sans', donateFontSansStack());
    root.style.setProperty('--donate-font-mono', donateFontMonoStack());
  }

  private clearDocumentDonateFonts(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const root = document.documentElement;
    root.classList.remove('donate-app--custom-fonts');
    root.style.removeProperty('--donate-font-sans');
    root.style.removeProperty('--donate-font-mono');
  }
}
